import React from "react"
import throttle from "lodash/throttle"
import { observer } from "mobx-react-lite"

import EntityPageHeaderLayout from "@components/layout/EntityPageHeaderLayout/EntityPageHeaderLayout"
import LoadingCover from "@components/ui/Loader/LoadingCover"
import NotFound from "@components/ui/NotFound/NotFound"
import Pagination from "@components/ui/Pagination/Pagination"
import usePagination from "@components/ui/Pagination/usePagination"
import { useController, useStore } from "@store/index"
import {
  TableOrderContext,
  useTableOrder,
} from "@components/ui/BaseTable/TableOrderContext"
import { SortBy } from "@framework/types/common"

import UsersTable from "../components/UsersTable"

import styles from "./Users.module.sass"

export const ActiveUser: React.FC<{ query?: string }> = observer(
  ({ query }) => {
    const { adminUsersController } = useController()
    const { adminUsersStore } = useStore()

    const paginationProps = usePagination({
      pageSize: adminUsersStore.meta.pageSize,
      totalRows: adminUsersStore.meta.total,
      totalPages: adminUsersStore.meta.totalPages,
    })

    const pageNum = paginationProps.forcePage ?? 0
    const { pageSize } = adminUsersStore.meta

    const [orderBy, setOrder] = React.useState<SortBy | null>({
      name: "firstName",
      direction: true,
    })

    const orderContext = useTableOrder({ orderBy, onOrderChange: setOrder })

    const handleLoadUsers = React.useCallback(
      throttle(adminUsersController.loadActiveUsersPage, 300),
      []
    )

    const needReload = adminUsersStore.pages.size === 0

    React.useEffect(() => {
      handleLoadUsers(
        {
          pageNum,
          pageSize,
          query,
        },
        orderBy
      )
    }, [query, pageNum, pageSize, needReload, orderBy])

    const userIds = adminUsersStore.getPage(pageNum)
    const nothingIsFound = userIds.length === 0

    return (
      <TableOrderContext.Provider value={orderContext}>
        <LoadingCover
          isLoading={adminUsersStore.isLoading}
          className={styles.body}
        >
          {nothingIsFound && !adminUsersStore.isLoading ? (
            <NotFound>
              No users found
              {query ? (
                <>
                  &nbsp;by query <b>&ldquo;{query}&rdquo;</b>
                </>
              ) : null}
            </NotFound>
          ) : (
            <UsersTable rows={userIds} />
          )}
        </LoadingCover>

        <EntityPageHeaderLayout
          className={styles.footer}
          right={<Pagination {...paginationProps} />}
        />
      </TableOrderContext.Provider>
    )
  }
)

export default ActiveUser
